import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCurrentShop } from "@/lib/auth";
import { getAdvancedAnalysis, getInventoryAnalysisData } from "@/lib/api/inventory-api";
import { getMovementStats } from "@/lib/api/movements-api";
import { getShopDashboardStats } from "@/lib/api/reports-api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, BarChart3, Package, TrendingUp, AlertTriangle, FileDown, Printer } from "lucide-react";

const DATE_PRESETS: Record<string, { label: string; getRange: () => { startDate: string; endDate: string } }> = {
  thisMonth: {
    label: "Este mes",
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { startDate: start.toISOString(), endDate: end.toISOString() };
    },
  },
  lastMonth: {
    label: "Mes pasado",
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: start.toISOString(), endDate: end.toISOString() };
    },
  },
  last30: {
    label: "Últimos 30 días",
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      return { startDate: start.toISOString(), endDate: end.toISOString() };
    },
  },
  last90: {
    label: "Últimos 90 días",
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 90);
      return { startDate: start.toISOString(), endDate: end.toISOString() };
    },
  },
};

const PIE_COLORS = ["#22c55e", "#3b82f6", "#eab308", "#ef4444"];

function formatCurrency(n: number): string {
  return `$${n.toLocaleString("es-CU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function KpiCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="p-3 sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] sm:text-sm text-muted-foreground truncate">{label}</p>
            <p className="text-base sm:text-2xl font-bold mt-0.5 sm:mt-1 truncate">{value}</p>
            {sub && <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 truncate">{sub}</p>}
          </div>
          <div className="p-1.5 sm:p-2 rounded-lg bg-muted/50 shrink-0">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ShopInventoryAnalytics() {
  const shop = getCurrentShop();
  const shopId = shop?.id;
  const shopName = shop?.name || "Tienda";

  const [datePreset, setDatePreset] = useState("thisMonth");
  const [activeTab, setActiveTab] = useState("resumen");

  const dateRange = useMemo(() => DATE_PRESETS[datePreset]?.getRange() || DATE_PRESETS.thisMonth.getRange(), [datePreset]);

  const advancedQuery = useQuery({
    queryKey: ["advanced-analysis", shopId],
    queryFn: () => getAdvancedAnalysis(shopId!),
    enabled: !!shopId,
  });

  const analysisQuery = useQuery({
    queryKey: ["analysis-charts", shopId],
    queryFn: () => getInventoryAnalysisData(shopId!),
    enabled: !!shopId,
  });

  const dashQuery = useQuery({
    queryKey: ["dashboard-stats", shopId, dateRange],
    queryFn: () => getShopDashboardStats(shopId!, dateRange),
    enabled: !!shopId,
  });

  const movementStatsQuery = useQuery({
    queryKey: ["movement-stats", shopId, dateRange],
    queryFn: () => getMovementStats(shopId!, dateRange),
    enabled: !!shopId,
  });

  const loading = advancedQuery.isLoading || analysisQuery.isLoading || dashQuery.isLoading || movementStatsQuery.isLoading;
  const adv = advancedQuery.data;
  const analysis = analysisQuery.data;
  const dash = dashQuery.data?.data;
  const movStats = movementStatsQuery.data;

  if (!shopId) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-muted-foreground">Selecciona una tienda para ver los análisis.</div>
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PageContainer>
    );
  }

  const totalCost = adv?.summary.totalValueAtCost || 0;
  const totalPrice = adv?.summary.totalValueAtPrice || 0;
  const potentialProfit = totalPrice - totalCost;
  const margin = adv?.summary.averageMargin || 0;
  const productCount = adv?.summary.totalItems || 0;
  const alertCount = (adv?.summary.zeroStockCount || 0) + (adv?.summary.lowStockCount || 0) + (adv?.summary.expiringSoonCount || 0);

  const movementData = movStats
    ? [
        { name: "Entradas", quantity: movStats.entriesQuantity, cost: movStats.entriesCost, total: movStats.totalEntries },
        { name: "Salidas", quantity: movStats.exitsQuantity, cost: movStats.exitsCost, total: movStats.totalExits },
        { name: "Mermas", quantity: 0, cost: movStats.wasteCost, total: movStats.totalWaste },
      ]
    : [];

  const totalMovementCost =
    (movStats?.entriesCost || 0) + (movStats?.exitsCost || 0) + (movStats?.wasteCost || 0);

  return (
    <PageContainer className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-lg sm:text-2xl font-heading font-bold uppercase">Análisis y Reportes</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">{shopName}</p>
        </div>
        <div className="flex flex-col xs:flex-row gap-2">
          <Select value={datePreset} onValueChange={setDatePreset}>
            <SelectTrigger className="w-full xs:w-40 h-9 sm:h-10 text-xs sm:text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(DATE_PRESETS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled title="Exportar Excel" className="h-9 sm:h-10">
              <FileDown className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline ml-1.5">Exportar</span>
            </Button>
            <Button variant="outline" size="sm" disabled title="Imprimir" className="h-9 sm:h-10">
              <Printer className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline ml-1.5">Imprimir</span>
            </Button>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
          <TabsTrigger value="resumen" className="text-xs sm:text-sm">Resumen</TabsTrigger>
          <TabsTrigger value="movimientos" className="text-xs sm:text-sm">Movimientos</TabsTrigger>
          <TabsTrigger value="valoracion" className="text-xs sm:text-sm">Valoración</TabsTrigger>
          <TabsTrigger value="alertas" className="text-xs sm:text-sm">Alertas</TabsTrigger>
        </TabsList>

        {/* ████████████████████████████████████████████████████████ */}
        {/* PESTAÑA: RESUMEN                                        */}
        {/* ████████████████████████████████████████████████████████ */}
        <TabsContent value="resumen" className="pt-3 sm:pt-6 space-y-4 sm:space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
            <KpiCard icon={<BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />} label="Valor Inventario" value={formatCurrency(totalCost)} sub={`Venta: ${formatCurrency(totalPrice)}`} />
            <KpiCard icon={<Package className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />} label="Productos" value={String(productCount)} />
            <KpiCard icon={<TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />} label="Margen Prom." value={`${margin.toFixed(1)}%`} />
            <KpiCard icon={<AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />} label="Alertas" value={String(alertCount)} sub={`${adv?.summary.lowStockCount || 0} stock · ${adv?.summary.expiringSoonCount || 0} vence`} />
          </div>

          {analysis && analysis.categoryChartData && analysis.categoryChartData.length > 0 && (
            <Card>
              <CardHeader className="px-4 sm:px-6 py-3 sm:py-4">
                <CardTitle className="text-sm sm:text-base">Valor del Inventario por Categoría</CardTitle>
              </CardHeader>
              <CardContent className="px-2 sm:px-6">
                <div className="h-52 sm:h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analysis.categoryChartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="name" fontSize={10} tick={{ fill: "var(--muted-foreground)" }} />
                      <YAxis fontSize={10} tick={{ fill: "var(--muted-foreground)" }} />
                      <Tooltip formatter={(v: number) => formatCurrency(v)} />
                      <Bar dataKey="valor" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Valor venta" />
                      <Bar dataKey="costo" fill="#22c55e" radius={[4, 4, 0, 0]} name="Valor costo" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <Card>
              <CardHeader className="px-4 sm:px-6 py-3 sm:py-4">
                <CardTitle className="text-sm sm:text-base">Top Productos Vendidos</CardTitle>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                {dash?.topProducts && dash.topProducts.length > 0 ? (
                  <ul className="space-y-1.5 sm:space-y-2">
                    {dash.topProducts.slice(0, 5).map((p: any, i: number) => (
                      <li key={p.productId} className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="truncate mr-2">
                          <span className="font-mono text-muted-foreground mr-1 sm:mr-2">{i + 1}.</span>
                          {p.productName || p.productId}
                        </span>
                        <span className="font-medium whitespace-nowrap">{p.totalQuantity} und</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs sm:text-sm text-muted-foreground">No hay datos de ventas en el período.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="px-4 sm:px-6 py-3 sm:py-4">
                <CardTitle className="text-sm sm:text-base">Stock Bajo</CardTitle>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                {adv?.lowStockItems && adv.lowStockItems.length > 0 ? (
                  <ul className="space-y-1.5 sm:space-y-2">
                    {adv.lowStockItems.slice(0, 5).map((item: any) => (
                      <li key={item.id} className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="truncate mr-2">
                          <AlertTriangle className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-amber-500 inline mr-1 sm:mr-1.5 -mt-0.5" />
                          {item.product?.name || item.productId}
                        </span>
                        <span className="text-red-600 font-medium whitespace-nowrap">{item.stock} und</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs sm:text-sm text-muted-foreground">No hay productos con stock bajo.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ████████████████████████████████████████████████████████ */}
        {/* PESTAÑA: MOVIMIENTOS                                    */}
        {/* ████████████████████████████████████████████████████████ */}
        <TabsContent value="movimientos" className="pt-3 sm:pt-6 space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="px-4 sm:px-6 py-3 sm:py-4">
              <CardTitle className="text-sm sm:text-base">Resumen de Movimientos</CardTitle>
            </CardHeader>
            <CardContent className="px-2 sm:px-6">
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="p-2 sm:p-3 font-medium">Tipo</th>
                      <th className="p-2 sm:p-3 text-right font-medium">Cantidad</th>
                      <th className="p-2 sm:p-3 text-right font-medium">Valor</th>
                      <th className="p-2 sm:p-3 text-right font-medium">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movementData.map((row) => {
                      const pct = totalMovementCost > 0 ? ((row.cost / totalMovementCost) * 100).toFixed(1) : "0.0";
                      return (
                        <tr key={row.name} className="border-b last:border-0">
                          <td className="p-2 sm:p-3 font-medium">{row.name}</td>
                          <td className="p-2 sm:p-3 text-right">{row.total}</td>
                          <td className="p-2 sm:p-3 text-right">{formatCurrency(row.cost)}</td>
                          <td className="p-2 sm:p-3 text-right">{pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalMovementCost === 0 && (
                <p className="text-xs sm:text-sm text-muted-foreground py-3 sm:py-4 text-center">No hay movimientos en el período seleccionado.</p>
              )}
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled className="h-8 sm:h-9 text-xs sm:text-sm">
              <FileDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" /> Exportar Excel
            </Button>
            <Button variant="outline" size="sm" disabled className="h-8 sm:h-9 text-xs sm:text-sm">
              <Printer className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" /> Generar PDF
            </Button>
          </div>
        </TabsContent>

        {/* ████████████████████████████████████████████████████████ */}
        {/* PESTAÑA: VALORACIÓN                                     */}
        {/* ████████████████████████████████████████████████████████ */}
        <TabsContent value="valoracion" className="pt-3 sm:pt-6 space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <Card>
              <CardContent className="p-3 sm:p-5 text-center">
                <p className="text-xs sm:text-sm text-muted-foreground">Valor al Costo</p>
                <p className="text-lg sm:text-2xl font-bold mt-0.5 sm:mt-1">{formatCurrency(totalCost)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-5 text-center">
                <p className="text-xs sm:text-sm text-muted-foreground">Valor Potencial (Venta)</p>
                <p className="text-lg sm:text-2xl font-bold mt-0.5 sm:mt-1">{formatCurrency(totalPrice)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-5 text-center">
                <p className="text-xs sm:text-sm text-muted-foreground">Ganancia Potencial</p>
                <p className="text-lg sm:text-2xl font-bold mt-0.5 sm:mt-1">{formatCurrency(potentialProfit)}</p>
                <p className="text-xs text-muted-foreground mt-0.5 sm:mt-1">
                  {totalCost > 0 ? `(${((potentialProfit / totalCost) * 100).toFixed(1)}% margen)` : ""}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="px-4 sm:px-6 py-3 sm:py-4">
              <CardTitle className="text-sm sm:text-base">Distribución de Márgenes</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 px-2 sm:px-6">
              {analysis?.marginChartData && analysis.marginChartData.length > 0 && (
                <>
                  <div className="h-52 sm:h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={analysis.marginChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, value }: any) => `${value}`}>
                          {analysis.marginChartData.map((_: any, i: number) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-1.5 sm:space-y-2">
                    {analysis.marginChartData.map((d: any, i: number) => (
                      <div key={d.category} className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="flex items-center gap-1.5 sm:gap-2">
                          <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full inline-block shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                          <span className="truncate">{d.name}</span>
                        </span>
                        <span className="font-medium shrink-0 ml-2">{d.value} prod.</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {(!analysis?.marginChartData || analysis.marginChartData.length === 0) && (
                <p className="text-xs sm:text-sm text-muted-foreground col-span-2 text-center py-6 sm:py-8">No hay datos de márgenes disponibles.</p>
              )}
            </CardContent>
          </Card>

          {analysis?.categoryChartData && analysis.categoryChartData.length > 0 && (
            <Card>
              <CardHeader className="px-4 sm:px-6 py-3 sm:py-4">
                <CardTitle className="text-sm sm:text-base">Valor por Categoría</CardTitle>
              </CardHeader>
              <CardContent className="px-2 sm:px-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs sm:text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="p-2 sm:p-3 font-medium">Categoría</th>
                        <th className="p-2 sm:p-3 text-right font-medium">Costo</th>
                        <th className="p-2 sm:p-3 text-right font-medium">Venta</th>
                        <th className="p-2 sm:p-3 text-right font-medium">Prod.</th>
                        <th className="p-2 sm:p-3 text-right font-medium">Margen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysis.categoryChartData.map((cat: any) => (
                        <tr key={cat.name} className="border-b last:border-0">
                          <td className="p-2 sm:p-3 font-medium truncate max-w-[100px] sm:max-w-none">{cat.name}</td>
                          <td className="p-2 sm:p-3 text-right">{formatCurrency(cat.costo)}</td>
                          <td className="p-2 sm:p-3 text-right">{formatCurrency(cat.valor)}</td>
                          <td className="p-2 sm:p-3 text-right">{cat.productos}</td>
                          <td className="p-2 sm:p-3 text-right">{cat.margenPromedio.toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ████████████████████████████████████████████████████████ */}
        {/* PESTAÑA: ALERTAS                                        */}
        {/* ████████████████████████████████████████████████████████ */}
        <TabsContent value="alertas" className="pt-3 sm:pt-6 space-y-4 sm:space-y-6">
          {adv?.zeroStockItems && adv.zeroStockItems.length > 0 && (
            <Card className="border-red-200">
              <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6 py-3 sm:py-4">
                <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                  <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-red-500 animate-pulse" />
                  Agotados ({adv.summary.zeroStockCount})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                <div className="space-y-2 sm:space-y-3">
                  {adv.zeroStockItems.map((item: any) => (
                    <div key={item.id} className="flex flex-col xs:flex-row xs:items-center justify-between text-xs sm:text-sm p-2 sm:p-3 bg-red-50 rounded-lg gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{item.product?.name || item.productId}</p>
                        <p className="text-xs text-muted-foreground">
                          Stock: <span className="font-semibold text-red-600">0</span>
                          {item.reorderPoint != null && <span> | Punto de reorden: {item.reorderPoint}</span>}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {adv?.lowStockItems && adv.lowStockItems.some((i: any) => i.stock > 0) && (
            <Card className="border-amber-200">
              <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6 py-3 sm:py-4">
                <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                  <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-amber-500" />
                  Stock Bajo ({adv.lowStockItems.filter((i: any) => i.stock > 0).length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                <div className="space-y-2 sm:space-y-3">
                  {adv.lowStockItems.filter((i: any) => i.stock > 0).map((item: any) => (
                    <div key={item.id} className="flex flex-col xs:flex-row xs:items-center justify-between text-xs sm:text-sm p-2 sm:p-3 bg-amber-50 rounded-lg gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{item.product?.name || item.productId}</p>
                        <p className="text-xs text-muted-foreground">
                          Stock: <span className="font-semibold text-amber-600">{item.stock}</span>
                          {item.reorderPoint != null && <span> | Punto de reorden: {item.reorderPoint}</span>}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {adv?.expiringSoon && adv.expiringSoon.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6 py-3 sm:py-4">
                <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                  <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-amber-500" />
                  Próximos a Vencer
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                <div className="space-y-2 sm:space-y-3">
                  {adv.expiringSoon.map((item: any) => {
                    const daysLeft = item.expiryDate ? Math.ceil((new Date(item.expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
                    return (
                      <div key={item.id} className="flex flex-col xs:flex-row xs:items-center justify-between text-xs sm:text-sm p-2 sm:p-3 bg-amber-50 rounded-lg gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate text-xs sm:text-sm">{item.productId}</p>
                          <p className="text-xs text-muted-foreground">
                            Stock: {item.stock}{daysLeft !== null && <span> · Vence en {daysLeft} días</span>}
                          </p>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          <Button size="sm" variant="outline" className="h-7 text-xs px-2 sm:px-3" disabled>Merma</Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs px-2" disabled>Desc.</Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {adv?.recommendations && adv.recommendations.length > 0 && (
            <Card>
              <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6 py-3 sm:py-4">
                <CardTitle className="text-sm sm:text-base">Recomendaciones</CardTitle>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                <ul className="space-y-1.5 sm:space-y-2">
                  {adv.recommendations.map((r: any, i: number) => (
                    <li key={i} className="flex items-start gap-1.5 sm:gap-2 text-xs sm:text-sm p-1.5 sm:p-2 rounded-lg bg-muted/30">
                      <Badge variant={r.priority === "HIGH" ? "destructive" : "secondary"} className="shrink-0 mt-0.5 text-[10px] sm:text-xs px-1.5 py-0">
                        {r.priority === "HIGH" ? "ALTA" : "MEDIA"}
                      </Badge>
                      <span className="text-muted-foreground">{r.message}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {alertCount === 0 && (
            <Card>
              <CardContent className="p-5 sm:p-8 text-center text-muted-foreground">
                <Package className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-1.5 sm:mb-2 opacity-40" />
                <p className="text-xs sm:text-sm">No hay alertas activas. Tu inventario está en buen estado.</p>
              </CardContent>
            </Card>
          )}

          <Card className="border-dashed">
            <CardContent className="p-3 sm:p-5 text-center text-xs sm:text-sm text-muted-foreground">
              <p className="font-medium">📉 Productos sin rotación (30+ días sin venta)</p>
              <p className="text-xs mt-0.5 sm:mt-1">Próximamente — Requiere endpoint de análisis de rotación.</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
